import { useCallback, useEffect, useRef, useState } from 'react'
import { checkTeamBotTokens, getSlackInstallUrl } from '../lib/supabase'
import { Button } from './ui/button'

export function SlackWorkspaceConnection({ teamId, onStatusChange, requireFreshStatus = false, canInstall = false }: { teamId: string; onStatusChange?: (connected: boolean | null) => void; requireFreshStatus?: boolean; canInstall?: boolean }) {
  const [status, setStatus] = useState<'loading' | 'connected' | 'missing' | 'error'>('loading')
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')
  const generation = useRef(0)
  const refresh = useCallback(async () => {
    const request = ++generation.current
    setStatus('loading')
    if (requireFreshStatus) onStatusChange?.(null)
    try {
      const result = await checkTeamBotTokens(teamId)
      if (request !== generation.current) return
      setStatus(result.hasBotToken ? 'connected' : 'missing')
      onStatusChange?.(result.hasBotToken)
    } catch {
      if (request !== generation.current) return
      setStatus('error'); onStatusChange?.(null)
    }
  }, [teamId, onStatusChange, requireFreshStatus])
  useEffect(() => {
    onStatusChange?.(null)
    void refresh()
    const visible = () => { if (document.visibilityState === 'visible') void refresh() }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', visible)
    return () => { generation.current++; window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', visible) }
  }, [refresh])
  const connect = async () => {
    if (connecting || !canInstall) return
    setConnecting(true); setError('')
    try {
      const latest = await checkTeamBotTokens(teamId)
      if (latest.hasBotToken) { await refresh(); return }
      const url = await getSlackInstallUrl(latest.slackTeamId)
      window.location.href = url
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to connect Slack. Please retry.') }
    finally { setConnecting(false) }
  }
  if (!canInstall) return <div className="space-y-2 text-sm text-muted-foreground" aria-label="Slack connection status">
    <p role="status">{status === 'connected' ? 'SyncSimple Slack app is installed' : status === 'loading' ? 'Checking Slack app installation...' : status === 'error' ? 'Could not check the Slack app installation' : 'Ask your workspace owner to install the SyncSimple Slack app.'}</p>
    {status === 'error' && <Button type="button" variant="outline" onClick={refresh}>Retry Slack status check</Button>}
  </div>
  return <div className="space-y-2 border border-border p-4" aria-label="Slack workspace connection">
    <p className="text-sm font-medium">{status === 'connected' ? 'SyncSimple Slack app is installed' : status === 'loading' ? 'Checking Slack app installation...' : status === 'error' ? 'Could not check the Slack app installation' : 'Install the SyncSimple Slack app'}</p>
    {status === 'missing' && <p className="text-sm text-muted-foreground">Add the SyncSimple bot to your Slack workspace to import members. You can enable standup reminders and summaries later. Only one installation is needed per workspace.</p>}
    <div className="flex flex-wrap gap-2">
      {status === 'missing' && <Button type="button" disabled={connecting} onClick={connect}>{connecting ? 'Opening Slack...' : 'Install SyncSimple Slack app'}</Button>}
      <Button type="button" variant="outline" onClick={refresh} disabled={status === 'loading' || connecting}>Check installation</Button>
    </div>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </div>
}
